import React, { useState, useEffect } from 'react';
import { TokenStatus } from '../stores/data/types';
import { TokenState } from '../api/fe-management-system-api/types';
import { RetrospectiveType } from '../api/fe-management-system-api/retrospective';
import RetrospectiveTypeChip from './RetrospectiveTypeChip';

interface EvaluationSummaryProps {
  totalScoreSum: number;
  summary?: string;
  retrospective?: string;
  tokenState?: TokenState;
  onSave?: (summary: string, retrospective: string) => Promise<void> | void;
  onReportLoss?: () => Promise<void> | void;
}

const EvaluationSummary: React.FC<EvaluationSummaryProps> = ({
  totalScoreSum,
  summary,
  retrospective,
  tokenState,
  onSave,
  onReportLoss,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState(summary || '');
  const [editedRetrospective, setEditedRetrospective] = useState(retrospective || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setEditedSummary(summary || '');
      setEditedRetrospective(retrospective || '');
    }
  }, [summary, retrospective, isEditing]);

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await onSave?.(editedSummary.trim(), editedRetrospective.trim());
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedSummary(summary || '');
    setEditedRetrospective(retrospective || '');
  };

  const shouldShowReportLoss =
    tokenState?.status === TokenStatus.SOLD &&
    !tokenState.retrospectiveTypes?.includes(RetrospectiveType.LOSS);

  const handleReportLossClick = async () => {
    if (!shouldShowReportLoss || !onReportLoss) {
      return;
    }
    const confirmed = window.confirm(
      '해당 토큰의 거래를 정말 손실로 기록하시겠습니까?',
    );
    if (confirmed) {
      await onReportLoss();
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
      <div className="flex flex-col gap-2 mb-3">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-semibold text-white">Overall Evaluation</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center space-x-1 rounded px-2 py-1 text-xs text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors"
              type="button"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
              <span>Edit</span>
            </button>
            {shouldShowReportLoss && (
              <button
                type="button"
                onClick={handleReportLossClick}
                className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-500 transition-colors"
              >
                손실 기록하기
              </button>
            )}
          </div>
        </div>
        {tokenState?.retrospectiveTypes && tokenState.retrospectiveTypes.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tokenState.retrospectiveTypes.map((type) => (
              <RetrospectiveTypeChip type={type} key={type} />
            ))}
          </div>
        )}
      </div>
      
      {/* Total Score Sum */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm text-gray-400">Total Score Sum:</span>
          <span className="text-xl font-bold text-white">
            {tokenState?.status === TokenStatus.REVIEWING ? 'Review in progress' : totalScoreSum}
          </span>
        </div>
      </div>

      {isEditing ? (
        <>
          <div className="mb-3">
            <label className="text-xs text-gray-400 mb-1 block">Summary</label>
            <textarea
              value={editedSummary}
              onChange={(e) => setEditedSummary(e.target.value)}
              className="w-full min-h-[80px] bg-gray-900 text-white px-3 py-2 rounded border border-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
              placeholder="Write an overall summary..."
            />
          </div>
          <div className="mb-3">
            <label className="text-xs text-gray-400 mb-1 block">Retrospective</label>
            <textarea
              value={editedRetrospective}
              onChange={(e) => setEditedRetrospective(e.target.value)}
              className="w-full min-h-[80px] bg-gray-900 text-white px-3 py-2 rounded border border-gray-700 focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
              placeholder="Capture any retrospective notes..."
            />
          </div>
          <div className="flex justify-end gap-2">
           <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className="px-3 py-1 text-xs rounded bg-gray-600 text-white hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          {summary && (
            <div className="mb-4">
              <div className="text-sm text-gray-400 mb-2">Summary:</div>
              <div className="text-base text-gray-300 bg-gray-700/50 rounded p-3 border-l-4 border-l-blue-500" style={{ 
                lineHeight: 1.5, wordBreak: "break-all",
                overflowWrap: "break-word"
              }}>
                {summary}
              </div>
            </div>
          )}
          {retrospective && (
            <div>
              <div className="text-sm text-gray-400 mb-2">Retrospective:</div>
              <div className="text-base text-gray-300 bg-gray-700/50 rounded p-3 border-l-4 border-l-purple-500" style={{ 
                lineHeight: 1.5, wordBreak: "break-all",
                overflowWrap: "break-word"
              }}>
                {retrospective}
              </div>
            </div>
          )}
          {!summary && !retrospective && (
            <div className="text-sm text-gray-500 italic">
              No summary or retrospective available.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EvaluationSummary;
